import { createServerFn } from "@tanstack/react-start";
import type { TrelloBoardResult, TrelloCard, TrelloList } from "@/lib/trello-types";

function classify(status: number, body: string, boardId: string): string {
  if (status === 401) return "Ogiltig API-nyckel eller token (401). Generera nya credentials i Trello.";
  if (status === 403) return "Token saknar behörighet till boarden (403). Kontrollera att token gäller rätt konto.";
  if (status === 404) return `Boarden hittades inte (404). TRELLO_BOARD_ID="${boardId}" matchar ingen board.`;
  if (status === 400)
    return `Trello svarar 400 "invalid id". Värdet "${boardId}" är inte ett giltigt board-id. Använd det långa hex-id:t (24 tecken) från https://trello.com/b/<short>/<namn>.json (fältet "id" längst upp).`;
  if (status === 429) return "För många anrop till Trello (429). Vänta en stund och försök igen.";
  if (status >= 500) return `Trello har problem just nu (${status}). Försök igen om en stund.`;
  return `Trello svarade med fel ${status}: ${body.slice(0, 160)}`;
}

function normalizeBoardId(raw: string): string {
  const trimmed = raw.trim();
  // Full URL e.g. https://trello.com/b/aBcD1234/mitt-brade
  const urlMatch = trimmed.match(/trello\.com\/b\/([^/?#]+)/i);
  if (urlMatch) return urlMatch[1];
  // Strip any leading /b/ if user pasted partial path
  return trimmed.replace(/^\/?b\//, "").replace(/\/.*$/, "");
}

export const getTrelloBoard = createServerFn({ method: "GET" }).handler(
  async (): Promise<TrelloBoardResult> => {
    const key = process.env.TRELLO_API_KEY;
    const token = process.env.TRELLO_TOKEN;
    const boardId = process.env.TRELLO_BOARD_ID;

    const missing = [
      !key && "TRELLO_API_KEY",
      !token && "TRELLO_TOKEN",
      !boardId && "TRELLO_BOARD_ID",
    ].filter(Boolean);
    if (missing.length) {
      return {
        data: null,
        error: `Saknade Trello-credentials: ${missing.join(", ")}. Lägg till dem i Lovable Cloud-secrets.`,
      };
    }

    const cleanId = normalizeBoardId(boardId!);
    const auth = `key=${encodeURIComponent(key!)}&token=${encodeURIComponent(token!)}`;
    const base = `https://api.trello.com/1/boards/${encodeURIComponent(cleanId)}`;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);

    try {
      const [boardRes, listsRes, cardsRes] = await Promise.all([
        fetch(`${base}?fields=name,url&${auth}`, { signal: controller.signal }),
        fetch(`${base}/lists?fields=name,pos&${auth}`, { signal: controller.signal }),
        fetch(
          `${base}/cards?filter=all&fields=name,desc,url,idList,due,dateLastActivity,labels,closed&${auth}`,
          { signal: controller.signal },
        ),
      ]);

      const failed = [boardRes, listsRes, cardsRes].find((r) => !r.ok);
      if (failed) {
        const body = await failed.text().catch(() => "");
        console.error(`Trello ${failed.status} for boardId="${cleanId}" (raw="${boardId}"): ${body.slice(0, 200)}`);
        return { data: null, error: classify(failed.status, body, cleanId) };
      }

      const board = (await boardRes.json()) as { name: string; url: string };
      const lists = (await listsRes.json()) as TrelloList[];
      const cards = (await cardsRes.json()) as TrelloCard[];

      return {
        data: {
          boardName: board.name,
          boardUrl: board.url,
          lists: lists.sort((a, b) => a.pos - b.pos),
          cards,
          fetchedAt: new Date().toISOString(),
        },
        error: null,
      };
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("Trello fetch failed", e);
      if (msg.includes("aborted")) {
        return { data: null, error: "Tidsgräns nådd – Trello svarade inte inom 10 sekunder." };
      }
      return { data: null, error: `Nätverksfel mot Trello: ${msg}` };
    } finally {
      clearTimeout(timeout);
    }
  },
);
